import { useState, useEffect, useCallback, useRef } from 'react'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import axios from 'axios'
import { fetchData } from '../../../utils/data.js'
import withConfig from '../../wrappers/withConfig.js'
import { format } from 'date-fns'
import PageNavigation from '../../../utils/PageNavigation'
import AdvancedSearchResults from './AdvancedSearchResults.js'
import BackButton from '../../elem/BackButton.js'
import DatepickerComponent from '../../datepicker.component.js'
import { Helmet } from 'react-helmet-async'

const DisclosureAdvanced = ({ config }) => {
    const apiURL = config.API_URL
    const cancelRef = useRef(null)

    // State management
    const [state, setState] = useState({
        query: '',
        results: {},
        formResults: {},
        totalCount: '',
        pageNo: 1,
        // state
        selectedState: null,
        stateNumber: '',
        state_name: '',
        stateNames: [],
        // county
        countyNumber: '',
        selectedCounty: null,
        countyNames: [],
        // ingredient
        ingredientNames: [],
        ingredientNameList: [],
        isLoading: true,
        // well
        wellNames: [],
        selectedWell: null,
        selectedWellName: '',
        wellname: '',
        // operator
        operatorNames: [],
        selectedOperator: '',
        Operator_No: '',
        // job dates
        jobstart: '',
        selectedjobstart: '',
        jobend: '',
        selectedjobend: '',
        cas_no: '',
        api_no: '',
        // pagination
        totalResults: 0,
        totalPages: 0,
        currentPageNo: 0,
        resultsPerPage: 200,
        nextPageNumber: '',
        loading: false,
        message: '',
    })

    // Utility functions
    const getPageCount = useCallback((total, denominator) => {
        const divisible = total % denominator === 0
        const valueToBeAdded = divisible ? 0 : 1
        return Math.floor(total / denominator) + valueToBeAdded
    }, [])

    const updateState = useCallback((updates) => {
        setState((prevState) => ({ ...prevState, ...updates }))
    }, [])

    // Pagination handler
    const handlePageClick = useCallback(
        (event, type) => {
            const updatedPageNo =
                type === 'prev'
                    ? state.currentPageNo - 1
                    : state.currentPageNo + 1

            if (!state.loading) {
                updateState({ loading: true, message: '' })
                handleSubmit(event, updatedPageNo, state.query)
            }
        },
        [state.currentPageNo, state.loading, state.query]
    )

    // API functions
    const populateWells = useCallback(
        (county_code) => {
            const wellSearchURL = `${apiURL}/fracfocus/wellnamesbystateandcountyids?countyid=${county_code}&stateid=${state.stateNumber}`

            const then = (data) => {
                const resultNotFound = !data.length
                    ? 'There are no more search results.'
                    : ''
                updateState({
                    results: data,
                    message: resultNotFound,
                    loading: false,
                    wellNames: data.data,
                })
            }

            const onError = (error) => {
                updateState({
                    loading: false,
                    message: 'Failed to fetch the data. Please check network',
                })
                console.log(error)
            }

            fetchData(wellSearchURL, then, onError)
        },
        [apiURL, state.stateNumber, updateState]
    )

    const populateCounty = useCallback(
        (state_no) => {
            const countySearchURL = `${apiURL}/state/countiesbystateid?stateid=${state_no}`

            const then = (data) => {
                const resultNotFound = !data.length
                    ? 'There are no more search results.'
                    : ''
                updateState({
                    results: data,
                    message: resultNotFound,
                    loading: false,
                    countyNames: data.data,
                })
            }

            const onError = (error) => {
                updateState({
                    loading: false,
                    message: 'Failed to fetch the data. Please check network',
                })
                console.log(error)
            }

            fetchData(countySearchURL, then, onError)
        },
        [apiURL, updateState]
    )

    // Event handlers
    const handleStateChange = useCallback(
        (event) => {
            updateState({ selectedState: event })

            if (event != null) {
                updateState({
                    selectedCounty: '',
                    stateNumber: event.value,
                    state_name: event.label,
                })
                populateCounty(event.value)
            } else {
                // Reset all dependent fields
                updateState({
                    stateNumber: '',
                    state_name: '',
                    countyNumber: '',
                    wellname: '',
                    countyNames: [],
                    wellNames: [],
                    selectedCounty: '',
                    selectedWell: '',
                })
            }
        },
        [populateCounty, updateState]
    )

    const handleCountyChange = useCallback(
        (event) => {
            updateState({ selectedCounty: event })

            if (event != null) {
                updateState({ countyNumber: event.value })
                populateWells(event.value)
            } else {
                updateState({
                    countyNumber: '',
                    wellname: '',
                    wellNames: [],
                    selectedWell: '',
                })
            }
        },
        [populateWells, updateState]
    )

    const handleWellChange = useCallback(
        (event) => {
            updateState({ selectedWell: event })

            if (event != null) {
                updateState({ wellname: event.value })
            } else {
                updateState({
                    wellname: '',
                    selectedWell: '',
                })
            }
        },
        [updateState]
    )

    const handleOperatorChange = useCallback(
        (event) => {
            updateState({ selectedOperator: event })

            if (event != null) {
                updateState({ Operator_No: event.value })
            } else {
                updateState({ Operator_No: '' })
            }
        },
        [updateState]
    )

    const handleJobStartChange = useCallback(
        (event) => {
            updateState({
                selectedjobstart: event,
                jobstart: event.target.value,
            })
        },
        [updateState]
    )

    const handleJobEndChange = useCallback(
        (event) => {
            updateState({
                selectedjobend: event,
                jobend: event.target.value,
            })
        },
        [updateState]
    )

    const handleWellNameChange = useCallback(
        (event) => {
            updateState({ wellname: event.target.value })
        },
        [updateState]
    )

    const handleCASChange = useCallback(
        (event) => {
            updateState({ cas_no: event.target.value })
        },
        [updateState]
    )

    const handleAPIChange = useCallback(
        (event) => {
            updateState({ api_no: event.target.value })
        },
        [updateState]
    )

    const handleIngredientChange = useCallback(
        (event) => {
            updateState({ ingredientNameList: event })
        },
        [updateState]
    )

    // Submit handler
    const handleSubmit = useCallback(
        (event, updatedPageNo = '', query) => {
            event.preventDefault()

           if (
                state.stateNumber === '' &&
                state.state_name === '' &&
                state.countyNumber === '' &&
                state.Operator_No === '' &&
                state.jobstart === '' &&
                state.jobend === '' &&
                state.wellname === '' &&
                state.cas_no === '' &&
                (!Array.isArray(state.ingredientNameList) || state.ingredientNameList.length === 0) &&
                state.api_no === ''
                ) {
                window.alert('No criteria entered.')
                return
            }

            const params = new URLSearchParams();

            if (state.stateNumber && Number.isInteger(+state.stateNumber) && +state.stateNumber >=1 && +state.stateNumber <= 51) {
                params.append('state_code', state.stateNumber);
            }

            if (state.state_name) params.append('state_name', state.state_name);
            if (state.countyNumber) params.append('county_code', state.countyNumber); 
            if (state.Operator_No) params.append('operator_name', state.Operator_No);
            if (state.jobstart) {
                params.append('start_date', state.jobstart);
            }
            if (state.jobend) {
                params.append('end_date', state.jobend);
            }
            if (state.wellname) params.append('well_name', state.wellname);
            if (state.cas_no) params.append('cas_number', state.cas_no);
            if (Array.isArray(state.ingredientNameList) && state.ingredientNameList.length > 0) {
                params.append('ingredient_name', state.ingredientNameList.map(x => x.value).join('$'));
            }
            if (state.api_no) params.append('api_number', state.api_no);

            params.append('page', updatedPageNo || 1);
            params.append('pagesize', 200);  

            const searchURL = `${apiURL}/fracfocus/search?${params.toString()}`;


            if (cancelRef.current) {
                cancelRef.current.cancel()
            }
            cancelRef.current = axios.CancelToken.source()

            axios
                .get(searchURL, {
                    cancelToken: cancelRef.current.token,
                })
                .then((res) => {
                    const total = res.data.Count
                    const totalPagesCount = getPageCount(total, 200)
                    const resultNotFound = !res.data.Wells
                        ? 'There are no more search results.'
                        : ''

                    updateState({
                        formResults: res.data.Wells,
                        totalResults: total,
                        currentPageNo: updatedPageNo,
                        nextPageNumber: updatedPageNo,
                        totalPages: totalPagesCount,
                        message: resultNotFound,
                        loading: false,
                    })
                })
                .catch((error) => {
                    if (axios.isCancel(error) || error) {
                        updateState({
                            loading: false,
                            message:
                                'Failed to fetch the data. Please check network',
                        })
                        console.log(error)
                    }
                })
        },
        [state, apiURL, getPageCount, updateState]
    )

    // Data fetching functions
    const fetchStates = useCallback(() => {
        const stateURL = `${apiURL}/state/states`

        const then = (data) => {
            updateState({ stateNames: data.data })
        }

        const onError = (error) => {
            console.log(error)
        }

        fetchData(stateURL, then, onError)
    }, [apiURL, updateState])

    const fetchOperators = useCallback(() => {
        const operatorURL = `${apiURL}/operator/operators`

        const then = (data) => {
            updateState({ operatorNames: data.data })
        }

        const onError = (error) => {
            console.log(error)
        }

        fetchData(operatorURL, then, onError)
    }, [apiURL, updateState])

    const fetchIngredients = useCallback(() => {
        const ingredientURL = `${apiURL}/fracfocus/ingredients`

        const then = (data) => {
            const ingredients =
                data && data.data
                    ? data.data
                          .sort((a, b) =>
                              a.IngredientName.localeCompare(b.Ingredient)
                          )
                          .map((x) => ({
                              label: x.Ingredient,
                              value: x.IngredientName,
                          }))
                    : []
            updateState({ ingredientNames: ingredients, isLoading: false })
        }

        const onError = (error) => {
            console.log(error)
        }

        fetchData(ingredientURL, then, onError)
    }, [apiURL, updateState])

    const resetForm = useCallback(() => {
        window.location.reload(false)
    }, [])

    // Effects
    useEffect(() => {
        fetchStates()
        fetchOperators()
        fetchIngredients()
    }, [fetchStates, fetchOperators, fetchIngredients])

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (cancelRef.current) {
                cancelRef.current.cancel()
            }
        }
    }, [])

    // Render variables
    const {
        selectedState,
        stateNames,
        selectedCounty,
        countyNames,
        wellNames,
        ingredientNames,
        ingredientNameList,
        selectedWell,
        operatorNames,
        selectedOperator,
        jobstart,
        selectedjobstart,
        jobend,
        selectedjobend,
        wellname,
        cas_no,
        api_no,
        loading,
        message,
        currentPageNo,
        totalPages,
        formResults,
    } = state

    const showPrevLink = 1 < currentPageNo
    const showNextLink = totalPages > currentPageNo
    const date = format(new Date(), 'MM-dd-yyyy')
    const wellDisclosureURL = `${apiURL}/`
    const showing_start =
        currentPageNo === 1 ? 1 : state.resultsPerPage * (currentPageNo - 1) + 1
    const showing_end = state.resultsPerPage * currentPageNo

    return (
        <div>
            <Helmet>
                <title>Advanced Search</title>
                <link rel="icon" href="/favicon.ico" />
            </Helmet>
            <div className="return">
                <BackButton label={'SWITCH TO MAP SEARCH'} />
            </div>
            <hr className="page_break" />
            <div className="advanced_form">
                <form
                    id="advanced_form_input"
                    onSubmit={(event) =>
                        handleSubmit(event, state.pageNo, state.query)
                    }
                >
                    <h2 className="findwells">Advanced Search</h2>
                    <div className="advanced_row_form">
                        <div className="advanced_input">
                            <label>State</label>
                            <Select
                                options={stateNames.map((x) => ({
                                    label: x.StateName,
                                    value: x.State_No,
                                }))}
                                value={selectedState}
                                onChange={handleStateChange}
                                isClearable={true}
                                placeholder="Select A State"
                            />
                        </div>
                        <div className="advanced_input">
                            <label>County</label>
                            <Select
                                options={countyNames.map((x) => ({
                                    label: x.CountyName,
                                    value: x.Code,
                                }))}
                                value={selectedCounty}
                                onChange={handleCountyChange}
                                isDisabled={!selectedState}
                                isClearable={true}
                                placeholder="Select A County"
                            />
                        </div>
                        <div className="advanced_input">
                            <label>Wells Within County</label>
                            <br />
                            <Select
                                options={wellNames.map((x) => ({
                                    label: x.WellName,
                                    value: x.WellName,
                                }))}
                                value={selectedWell}
                                onChange={handleWellChange}
                                isDisabled={!selectedCounty}
                                isClearable={true}
                                placeholder="Select A Well"
                            />
                        </div>{' '}
                        <br /> <br />
                        <div className="advanced_input">
                            <label>Operator</label>
                            <br />
                            <Select
                                options={operatorNames.map((x) => ({
                                    label: x.OperatorName,
                                    value: x.OperatorName,
                                }))}
                                value={selectedOperator}
                                onChange={handleOperatorChange}
                                isClearable={true}
                                placeholder="Select An Operator"
                            />
                        </div>
                        <div className="advanced_date">
                            <label>Job Date Range</label>
                            <br />
                            <DatepickerComponent
                                selected={selectedjobstart}
                                value={jobstart}
                                onChange={handleJobStartChange}
                            />
                            to:
                            <DatepickerComponent
                                selected={selectedjobend}
                                value={jobend}
                                onChange={handleJobEndChange}
                            />
                        </div>
                        <div className="advanced_input">
                            <label>Well Name</label>
                            <br />
                            <input
                                className="advanced_input_text"
                                type="text"
                                value={wellname}
                                onChange={handleWellNameChange}
                                placeholder="Enter a Well Name"
                            />
                        </div>
                        <br /> <br />
                        <div className="advanced_input">
                            <label>CAS Number</label>
                            <br />
                            <input
                                className="advanced_input_text"
                                type="text"
                                value={cas_no}
                                onChange={handleCASChange}
                                placeholder="Enter a CAS Number"
                            />
                        </div>
                        <div className="advanced_input">
                            <label>API Well Number</label>
                            <br />
                            <input
                                className="advanced_input_text"
                                type="text"
                                value={api_no}
                                onChange={handleAPIChange}
                                placeholder="Enter an API Number"
                            />
                        </div>
                        <div className="advanced_input_ing">
                            <label>Ingredients</label>
                            <br />
                            <AsyncSelect
                                height={70}
                                autosize={true}
                                isLoading={state.isLoading}
                                defaultOptions={ingredientNames.slice(0, 50)}
                                loadOptions={(inputValue, callback) => {
                                    callback(
                                        ingredientNames
                                            .filter((x) =>
                                                x.label
                                                    .toLowerCase()
                                                    .includes(
                                                        inputValue.toLowerCase()
                                                    )
                                            )
                                            .slice(0, 50)
                                    )
                                }}
                                isMulti
                                value={ingredientNameList}
                                onChange={handleIngredientChange}
                                placeholder="Select An Ingredient"
                            />
                        </div>
                    </div>
                    <br />
                    {/* buttons */}
                    <button
                        className="advanced_search_button"
                        type="submit"
                        aria-hidden="true"
                    >
                        <span className="link_white">Search Jobs</span>
                    </button>
                    <button
                        onClick={resetForm}
                        className="advanced_search_reset"
                    >
                        <span className="link_white">Reset</span>
                    </button>
                </form>
            </div>
            <br />

            {/* Results */}
            {formResults ? (
                <div>
                    {/* Results Component  */}
                    <AdvancedSearchResults
                        formResults={formResults}
                        totalhits={state.totalResults}
                        wellURL={wellDisclosureURL}
                        show_start={showing_start}
                        show_end={showing_end}
                    />

                    {totalPages > 1 ? (
                        <PageNavigation
                            totalPages={totalPages}
                            currentPage={currentPageNo}
                            loading={loading}
                            showPrevLink={showPrevLink}
                            showNextLink={showNextLink}
                            show_start={showing_start}
                            show_end={showing_end}
                            total={state.totalResults}
                            type="JOBS"
                            handlePrevClick={(event) =>
                                handlePageClick(event, 'prev')
                            }
                            handleNextClick={(event) =>
                                handlePageClick(event, 'next')
                            }
                        />
                    ) : (
                        ''
                    )}
                </div>
            ) : (
                ''
            )}
        </div>
    )
}

export default withConfig(DisclosureAdvanced)
